import { AlertTriangle, RotateCcw } from "lucide-react";
import { Component, type ErrorInfo, type ReactNode } from "react";

import { getPosthogSync } from "../../lib/posthog";

interface Props {
  children: ReactNode;
  /** Shown in the error card title. Defaults to "Something went wrong" */
  label?: string;
  /** If true, renders a compact inline error instead of a full-panel card */
  inline?: boolean;
  /** Optional fallback to render instead of the default error UI */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      `[ErrorBoundary${this.props.label ? `: ${this.props.label}` : ""}]`,
      error,
      info.componentStack
    );
    getPosthogSync()?.captureException?.(error, {
      componentStack: info.componentStack,
      label: this.props.label,
    });
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    if (this.props.inline) {
      return (
        <div className="flex items-center gap-2 p-3 text-sm text-trace-red">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span className="truncate">
            {this.state.error?.message || "Component error"}
          </span>
          <button
            onClick={this.handleRetry}
            className="btn-ghost text-xs shrink-0"
          >
            Retry
          </button>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center h-full p-6">
        <div className="max-w-md text-center space-y-4">
          {/* PCB-themed error icon */}
          <div className="mx-auto w-16 h-16 rounded-full bg-trace-red/10 border border-trace-red/20 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-trace-red" />
          </div>

          <div>
            <h3 className="text-sm font-medium text-ratsnest">
              {this.props.label || "Something went wrong"}
            </h3>
            <p className="mt-1 text-xs text-ratsnest-dim">
              {this.state.error?.message || "An unexpected error occurred"}
            </p>
          </div>

          <button onClick={this.handleRetry} className="btn-secondary text-sm">
            <RotateCcw className="w-3.5 h-3.5" />
            Try again
          </button>
        </div>
      </div>
    );
  }
}
